import { Alert, Box, Link } from '@mui/joy';
import { Warning } from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { SlidingPanel, Tabs } from 'core/components';
import { ITab } from 'core/types';
import {
  AccessList,
  AdminUserList,
  CostingCalculationList,
  DocumentList,
  PartNumberList,
  PricingProfileDetailsInfo,
  ProjectCustomerList,
  RuleList,
} from 'pricing/components';
import { PricingProfileDetailsTab, PricingProfileType } from 'pricing/enums';
import { pricingProfileDetailsPath } from 'pricing/routes/routePaths';
import { IPricingProfile } from 'pricing/types';
import { TAB_FULL_WIDTH } from 'theme/constants';
import { Color, Variant } from 'theme/enums';

import { PageSubtitle } from './PageSubtitle';

interface IProps {
  pricingProfile: IPricingProfile | null;
  projectId?: number;
  projectPricingProfileId?: number;
  isOpen: boolean;
  onClose: () => void;
}

export const PricingProfileDetailsDrawer = ({
  pricingProfile,
  projectId,
  projectPricingProfileId,
  isOpen,
  onClose,
}: IProps) => {
  const { t } = useTranslation();

  const isProjectType = pricingProfile?.type === PricingProfileType.PROJECT;
  const isReadOnly = !isProjectType;
  const hasProjectCustomersTab = !!(projectId && projectPricingProfileId);

  const defaultTab =
    isReadOnly && hasProjectCustomersTab
      ? PricingProfileDetailsTab.PROJECT_CUSTOMERS
      : PricingProfileDetailsTab.PART_NUMBER_BASED;

  const [activeTab, setActiveTab] = useState<string | number>(defaultTab);

  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  const onTabChange = useCallback((value: string | number) => {
    setActiveTab(value);
  }, []);

  const showReadOnlyWarning =
    isReadOnly && activeTab !== PricingProfileDetailsTab.PROJECT_CUSTOMERS;

  const tabs: ITab[] = useMemo(() => {
    if (!pricingProfile) {
      return [];
    }

    return [
      {
        label: t('labelPartNumberBased'),
        content: <PartNumberList isReadOnly={isReadOnly} pricingProfile={pricingProfile} />,
        value: PricingProfileDetailsTab.PART_NUMBER_BASED,
        style: { width: TAB_FULL_WIDTH },
      },
      {
        label: t('labelRuleBased'),
        content: <RuleList isReadOnly={isReadOnly} pricingProfile={pricingProfile} />,
        value: PricingProfileDetailsTab.RULE_BASED,
        style: { width: TAB_FULL_WIDTH },
      },
      {
        label: t('labelCostingCalculation'),
        content: <CostingCalculationList pricingProfile={pricingProfile} isReadOnly={isReadOnly} />,
        value: PricingProfileDetailsTab.COSTING_CALCULATION,
        style: { width: TAB_FULL_WIDTH },
      },
      ...(!isProjectType
        ? [
            {
              label: t('labelAccess'),
              content: <AccessList isReadOnly={isReadOnly} pricingProfile={pricingProfile} />,
              value: PricingProfileDetailsTab.ACCESS,
              style: { width: TAB_FULL_WIDTH },
            },
          ]
        : []),
      {
        label: t('labelDocuments'),
        content: <DocumentList pricingProfile={pricingProfile} isReadOnly={isReadOnly} />,
        value: PricingProfileDetailsTab.DOCUMENTS,
      },
      {
        label: t('labelAdminUsers'),
        content: <AdminUserList isReadOnly={isReadOnly} pricingProfile={pricingProfile} />,
        value: PricingProfileDetailsTab.ADMIN_USERS,
      },
      ...(projectId && projectPricingProfileId
        ? [
            {
              label: t('labelProjectCustomers'),
              content: (
                <ProjectCustomerList
                  pricingProfile={pricingProfile}
                  projectId={projectId}
                  projectPricingProfileId={projectPricingProfileId}
                />
              ),
              value: PricingProfileDetailsTab.PROJECT_CUSTOMERS,
            },
          ]
        : []),
      {
        label: t('labelDetails'),
        content: (
          <PricingProfileDetailsInfo pricingProfile={pricingProfile} isReadOnly={isReadOnly} />
        ),
        value: PricingProfileDetailsTab.DETAILS,
      },
    ];
  }, [isProjectType, isReadOnly, pricingProfile, projectId, projectPricingProfileId, t]);

  return (
    <SlidingPanel
      isOpen={isOpen}
      onClosePanel={onClose}
      title={pricingProfile?.name}
      subtitle={<PageSubtitle pricingProfile={pricingProfile} />}
      autoWidth
    >
      {showReadOnlyWarning && (
        <Alert
          startDecorator={<Warning />}
          variant={Variant.SOFT}
          color={Color.WARNING}
          sx={{ mt: 2, width: 'fit-content' }}
        >
          <Trans
            i18nKey="warningPricingProfileReadOnly"
            components={{
              wrapper: <Box />,
              linkWrapper: (
                <Link component={RouterLink} to={pricingProfileDetailsPath(pricingProfile?.id)} />
              ),
            }}
          />
        </Alert>
      )}
      <Tabs
        tabs={tabs}
        ariaLabel="pricingProfileDetails"
        defaultTab={defaultTab}
        onChange={onTabChange}
      />
    </SlidingPanel>
  );
};
